import { useCallback, useEffect, useRef, useState } from "react";
import { useAppDispatch, useAppSelector } from "../../../common/state/hooks";
import { currentUserSelector } from "../../../common/state/selectors/authSelector";
import {
  selectEnduserStatsCachedForUserId,
  selectCachedModulesForBuildId,
  setCachedUserId,
  setCachedModulesBuildId,
  invalidateEnduserStatsCache,
  invalidateModulesCache,
} from "../state/enduserStatsSlice";

/**
 * Hook to manage enduser stats cache based on current user.
 * Invalidates cache when user changes and tracks if data should be refetched.
 * Also triggers refetch when page regains focus (visibility change).
 *
 * @returns {object} - { shouldRefetch, markAsFetched }
 *   - shouldRefetch: true if data should be fetched (user changed or no cache)
 *   - markAsFetched: call this after successful fetch to update cache metadata
 */
export const useEnduserStatsCache = () => {
  const dispatch = useAppDispatch();
  const currentUser = useAppSelector(currentUserSelector);
  const cachedForUserId = useAppSelector(selectEnduserStatsCachedForUserId);
  const hasInvalidatedRef = useRef(false);
  const [visibilityRefetchTrigger, setVisibilityRefetchTrigger] = useState(0);

  const currentUserId = currentUser?.id ?? null;

  // Check if we need to invalidate cache (user changed)
  useEffect(() => {
    if (currentUserId && cachedForUserId && currentUserId !== cachedForUserId) {
      // User changed, invalidate cache
      if (!hasInvalidatedRef.current) {
        hasInvalidatedRef.current = true;
        dispatch(invalidateEnduserStatsCache());
      }
    } else {
      hasInvalidatedRef.current = false;
    }
  }, [currentUserId, cachedForUserId, dispatch]);

  // Refetch when page regains focus (user comes back to tab)
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        // Trigger refetch by incrementing the trigger
        setVisibilityRefetchTrigger((prev) => prev + 1);
        // Also invalidate modules cache so they refresh too
        dispatch(invalidateModulesCache());
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [dispatch]);

  // Should refetch if:
  // 1. No cached user ID (first load)
  // 2. Current user doesn't match cached user
  // 3. Visibility refetch was triggered (page regained focus)
  const shouldRefetch =
    !cachedForUserId ||
    (currentUserId !== null && currentUserId !== cachedForUserId) ||
    visibilityRefetchTrigger > 0;

  // Function to call after successful fetch to update cache metadata
  // Memoized to prevent infinite loops in useEffect dependencies
  const markAsFetched = useCallback(() => {
    if (currentUserId) {
      dispatch(setCachedUserId(currentUserId));
    }
    // Reset visibility trigger after fetch
    setVisibilityRefetchTrigger(0);
  }, [currentUserId, dispatch]);

  return { shouldRefetch, markAsFetched, currentUserId, cachedForUserId };
};

/**
 * Hook to manage modules cache for a specific buildId.
 * Only refetches if buildId changed or cache was invalidated (visibility change).
 *
 * @param buildId - The buildId of the parcours to fetch modules for
 * @returns {object} - { shouldRefetchModules, markModulesAsFetched }
 */
export const useModulesCache = (buildId: number | undefined) => {
  const dispatch = useAppDispatch();
  const cachedModulesForBuildId = useAppSelector(selectCachedModulesForBuildId);

  // Should refetch if:
  // 1. buildId is defined AND
  // 2. Either no cached buildId OR cached buildId is different
  const shouldRefetchModules =
    buildId !== undefined &&
    (cachedModulesForBuildId === null || cachedModulesForBuildId !== buildId);

  // Function to call after successful fetch
  const markModulesAsFetched = useCallback(() => {
    if (buildId !== undefined) {
      dispatch(setCachedModulesBuildId(buildId));
    }
  }, [buildId, dispatch]);

  return { shouldRefetchModules, markModulesAsFetched };
};
